import { useMutation } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { QrCode } from './QrCode.js';
import { Button, buttonClass, Notice, TextField } from './ui.js';

/**
 * A wrong code is not a wrong password (audit 12.10): it says so, and that the next one may do.
 * Ten wrong codes lock the sign-in for 15 minutes (SP-D19).
 */
export function codeError(error: unknown, t: TFunction) {
  if (!(error instanceof ApiError) || error.code !== 'INVALID_CREDENTIALS')
    return errorMessage(error, t);
  return (error.problem as { reason?: string }).reason === 'locked'
    ? t('mfa.locked')
    : t('mfa.wrong');
}

/**
 * The setup changed in another window meanwhile – turned on there, or started anew (SP-A2.174):
 * the server answers 409, which here means "reload", not «Das gibt es schon».
 */
const changedElsewhere = (error: unknown) => error instanceof ApiError && error.code === 'CONFLICT';

/** What the forms of a code share: the button that sends it, and what stands beside it. */
interface FormProps {
  submitLabel: string;
  onSubmit: (code: string) => void;
  busy: boolean;
  error: string | undefined;
  /** «danger» where the code lowers the protection (turning the factor off; SP-D19). */
  variant?: 'primary' | 'danger';
  /** Beside the button, e.g. «Abbrechen». */
  aside?: ReactNode;
}

/** The six digits of the authenticator app, «482 913» or without the space. */
export function CodeForm({
  submitLabel,
  onSubmit,
  busy,
  error,
  variant = 'primary',
  aside,
  focus = false,
}: FormProps & {
  /** Take the focus when shown (after «Einrichtung starten», whose button is gone then). */
  focus?: boolean;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const field = useRef<HTMLInputElement>(null);
  // Into the field when shown on request, and back to it with an error: read out with it.
  useEffect(() => {
    if (focus) field.current?.focus();
  }, [focus]);
  useEffect(() => {
    if (error) field.current?.focus();
  }, [error]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit(code.replace(/\s/g, ''));
  };
  return (
    <form onSubmit={submit}>
      <div className="code">
        <TextField
          label={t('mfa.code')}
          inputMode="numeric"
          autoComplete="one-time-code"
          ref={field}
          // As the apps show it, «482 913», or without the space.
          pattern="[0-9]{3} ?[0-9]{3}"
          maxLength={7}
          placeholder="••••••"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          error={error}
        />
      </div>
      <div className="acts">
        <Button type="submit" variant={variant} busy={busy}>
          {submitLabel}
        </Button>
        {aside}
      </div>
    </form>
  );
}

/** A recovery code instead of the app's (SP-D18): «K7Q2M-X9TAP», any case, dash optional. */
export function RecoveryForm({
  submitLabel,
  onSubmit,
  busy,
  error,
  variant = 'primary',
  aside,
}: FormProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    field.current?.focus();
  }, [error]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(code.trim());
      }}
    >
      <div className="code rec">
        <TextField
          label={t('mfa.recovery.code')}
          ref={field}
          autoComplete="off"
          spellCheck={false}
          placeholder="XXXXX-XXXXX"
          maxLength={16}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          error={error}
        />
      </div>
      <div className="acts">
        <Button type="submit" variant={variant} busy={busy}>
          {submitLabel}
        </Button>
        {aside}
      </div>
    </form>
  );
}

/**
 * A code of the app or, the phone gone, a recovery code (SP-D18): the forms and the link between
 * them. `head` shows the title for the kind of code asked.
 */
export function AnyCode({
  head,
  onSwitch,
  ...form
}: FormProps & {
  head: (recovery: boolean) => ReactNode;
  /** The other kind of code was chosen: an error of the last one no longer applies. */
  onSwitch: () => void;
}) {
  const { t } = useTranslation();
  const [recovery, setRecovery] = useState(false);
  return (
    <>
      {head(recovery)}
      {recovery ? <RecoveryForm {...form} /> : <CodeForm {...form} />}
      <button
        type="button"
        className="alt"
        onClick={() => {
          onSwitch();
          setRecovery(!recovery);
        }}
      >
        {recovery ? t('mfa.recovery.useApp') : t('mfa.recovery.use')}
      </button>
    </>
  );
}

/** Confirms the second factor of this session (POST /auth/mfa/verify). */
export function VerifyCode({
  head,
  onVerified,
}: {
  head: (recovery: boolean) => ReactNode;
  onVerified: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const verify = useMutation({
    mutationFn: (code: string) => unwrap(api.POST('/auth/mfa/verify', { body: { code } })),
    onSuccess: onVerified,
  });
  return (
    <AnyCode
      head={head}
      submitLabel={t('mfa.verify')}
      onSubmit={(code) => verify.mutate(code)}
      busy={verify.isPending}
      error={verify.error ? codeError(verify.error, t) : undefined}
      onSwitch={() => verify.reset()}
    />
  );
}

/**
 * A new set of recovery codes (SP-D18; account.html, admin/team.html «renew»): only with a code
 * of the app or a recovery code, as turning the factor off (review of SP-D21) – a session taken
 * over without the phone gets none. Wrong codes count as at the sign-in.
 */
export function RenewRecoveryCodes({
  onCodes,
  onCancel,
}: {
  onCodes: (recoveryCodes: string[]) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const renew = useMutation({
    mutationFn: (code: string) => unwrap(api.POST('/auth/mfa/recovery-codes', { body: { code } })),
    onSuccess: (data) => onCodes(data.recoveryCodes),
  });
  return (
    <div className="confirm mfa-card">
      <AnyCode
        head={(recovery) => (
          <>
            <b>{t('mfa.renew.title')}</b>
            <span className="hint">{recovery ? t('mfa.recovery.text') : t('mfa.renew.text')}</span>
          </>
        )}
        submitLabel={t('mfa.renew.submit')}
        onSubmit={(code) => renew.mutate(code)}
        busy={renew.isPending}
        error={renew.error ? codeError(renew.error, t) : undefined}
        onSwitch={() => renew.reset()}
        aside={
          <Button variant="secondary" onClick={onCancel}>
            {t('app.cancel')}
          </Button>
        }
      />
    </div>
  );
}

/**
 * Setting up the app (mfa.html «setup»; account.html): the setup starts on request, with the
 * password – a secret on every load would void a code already scanned (13.10), and a session taken
 * over must not bind its own app (SP-D19) – then the QR code or the key, then a first code. The
 * recovery codes it returns are shown once by the caller.
 */
export function SetupSecondFactor({
  intro,
  onEnabled,
}: {
  /** Before the start: why the app is needed here. */
  intro?: ReactNode;
  onEnabled: (recoveryCodes: string[]) => void;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const setup = useMutation({
    mutationFn: () => unwrap(api.POST('/auth/mfa/setup', { body: { password } })),
    onSuccess: () => setPassword(''),
  });
  const enable = useMutation({
    mutationFn: (code: string) => unwrap(api.POST('/auth/mfa/enable', { body: { code } })),
    onSuccess: (data) => onEnabled(data.recoveryCodes),
  });
  if (!setup.data) {
    const wrong = setup.error instanceof ApiError && setup.error.code === 'INVALID_CREDENTIALS';
    return (
      <>
        {intro}
        {setup.isError && !wrong ? (
          <Notice kind="error">
            {changedElsewhere(setup.error) ? t('mfa.setup.changed') : errorMessage(setup.error, t)}
          </Notice>
        ) : null}
        <form
          className="start"
          onSubmit={(e) => {
            e.preventDefault();
            setup.mutate();
          }}
        >
          <TextField
            label={t('mfa.setup.password')}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={wrong ? t('mfa.setup.wrongPassword') : undefined}
            required
          />
          <div className="acts">
            <Button type="submit" busy={setup.isPending}>
              {t('mfa.setup.start')}
            </Button>
          </div>
        </form>
      </>
    );
  }
  return (
    <>
      <p className="bodyM">{t('mfa.setup.scan')}</p>
      <div className="qr">
        <QrCode value={setup.data.otpauthUrl} label={t('mfa.setup.qrLabel')} />
        <div className="manual">
          <span className="hint">{t('mfa.setup.secret')}</span>
          <code className="key">{setup.data.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
          <a href={setup.data.otpauthUrl} className={buttonClass('secondary', 'sm')}>
            {t('mfa.setup.openApp')}
          </a>
        </div>
      </div>
      <CodeForm
        focus
        submitLabel={t('mfa.setup.enable')}
        onSubmit={(code) => enable.mutate(code)}
        busy={enable.isPending}
        error={
          !enable.error
            ? undefined
            : changedElsewhere(enable.error)
              ? t('mfa.setup.changed')
              : codeError(enable.error, t)
        }
      />
    </>
  );
}
